import React from "react";
import { Copy, Smartphone, Zap } from "lucide-react";

import { Card, Label, Hint, TextInput, Segmented, Toggle } from "./ui";

const OTP_TYPES = [
  {
    value: "COPY_CODE",
    label: "Copy code",
    description: "Customer taps to copy the code, then pastes it into your app.",
    icon: Copy,
  },
  {
    value: "ONE_TAP",
    label: "One-tap",
    description: "Android autofills the code into your app in one tap.",
    icon: Smartphone,
  },
  {
    value: "ZERO_TAP",
    label: "Zero-tap",
    description: "Android delivers the code to your app with no tap at all.",
    icon: Zap,
  },
];

const AuthenticationCard = ({ value, onChange }) => {
  const needsApp = value.otp_type !== "COPY_CODE";

  return (
    <Card className="space-y-5">
      <div>
        <Label>Code delivery</Label>
        <Segmented
          options={OTP_TYPES}
          value={value.otp_type}
          onChange={(otp_type) => onChange({ otp_type })}
          columns={1}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label>Button label</Label>
          <TextInput
            value={value.otp_button_text}
            maxLength={25}
            onChange={(e) => onChange({ otp_button_text: e.target.value })}
            placeholder="Copy code"
          />
        </div>
        <div>
          <Label hint="1–90, optional">Code expires after (minutes)</Label>
          <TextInput
            type="number"
            min={1}
            max={90}
            value={value.code_expiration_minutes ?? ""}
            onChange={(e) =>
              onChange({
                code_expiration_minutes: e.target.value
                  ? Number(e.target.value)
                  : "",
              })
            }
            placeholder="10"
          />
        </div>
      </div>

      <Toggle
        checked={!!value.add_security_recommendation}
        onChange={(add_security_recommendation) =>
          onChange({ add_security_recommendation })
        }
        label="Add security advice"
        description='Appends "For your security, do not share this code."'
      />

      {needsApp && (
        <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50/70 p-3.5">
          <p className="text-[12px] font-medium text-amber-800">
            One-tap and zero-tap need your Android app details
          </p>
          <div>
            <Label required>App package name</Label>
            <TextInput
              value={value.package_name}
              onChange={(e) => onChange({ package_name: e.target.value })}
              placeholder="com.example.myapp"
            />
          </div>
          <div>
            <Label required>App signing-key hash</Label>
            <TextInput
              value={value.signature_hash}
              onChange={(e) => onChange({ signature_hash: e.target.value })}
              placeholder="11-character hash"
            />
          </div>
          <Hint>
            Meta uses these to verify the code can only be autofilled into your
            app. Body text and footer are generated by Meta for authentication
            templates.
          </Hint>
        </div>
      )}

      {!needsApp && (
        <Hint>
          The message text and code are generated by Meta for authentication
          templates — you only choose the button and expiry.
        </Hint>
      )}
    </Card>
  );
};

export default AuthenticationCard;
